/**
 * RemusKonfigurator — modulare Auspuff-Zusammenstellung direkt auf der
 * Produktseite einer REMUS Komplettanlage.
 *
 * Funktion:
 *   - Lädt alle related_aliases (Komponenten der Komplettanlage)
 *   - Gruppiert sie nach product_group_name
 *     (Vorschalldämpfer / Mittelschalldämpfer / Endschalldämpfer /
 *      Endrohr / Verbindungsrohr / Adapter)
 *   - Zeigt für jede Gruppe ein Dropdown mit Auswahlmöglichkeiten
 *   - Berechnet Live-Gesamtpreis
 *   - "In den Warenkorb" fügt jede Komponente als EIGENE Position
 *     in den Warenkorb hinzu (kein Bundle).
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ShoppingCart, Settings2, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const COMPONENT_ORDER = [
  'Vorschalldämpfer',
  'Mittelschalldämpfer',
  'Verbindungsrohr',
  'Endschalldämpfer',
  'Endrohr',
  'Adapter',
];

function fmtPrice(p) {
  if (p == null || isNaN(p)) return '0,00 €';
  return p.toLocaleString('de-DE', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }) + ' €';
}

function groupByType(items) {
  const groups = {};
  for (const it of items) {
    const t = it.product_group_name || 'Sonstige';
    if (!groups[t]) groups[t] = [];
    groups[t].push(it);
  }
  return groups;
}

function sortedTypes(groups) {
  const known = COMPONENT_ORDER.filter(t => groups[t]);
  const others = Object.keys(groups).filter(t => !COMPONENT_ORDER.includes(t));
  return [...known, ...others];
}

export default function RemusKonfigurator({ product, onAddToCart }) {
  const aliases = useMemo(() => {
    if (!Array.isArray(product?.related_aliases)) return [];
    return product.related_aliases
      .map(r => (r && typeof r === 'object') ? r.alias : r)
      .filter(a => a && a !== product.alias);
  }, [product]);

  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({}); // { type: alias }
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    if (aliases.length === 0) {
      setComponents([]);
      return;
    }
    let abort = false;
    setLoading(true);
    fetch(`${API}/api/shop/products-by-aliases?aliases=${encodeURIComponent(aliases.join(','))}`)
      .then(r => r.ok ? r.json() : { items: [] })
      .then(data => { if (!abort) setComponents(data.items || []); })
      .catch(() => { if (!abort) setComponents([]); })
      .finally(() => { if (!abort) setLoading(false); });
    return () => { abort = true; };
  }, [aliases.join(',')]);

  const groups = useMemo(() => groupByType(components), [components]);
  const types = useMemo(() => sortedTypes(groups), [groups]);

  // Default-Auswahl: erstes Item pro Gruppe
  useEffect(() => {
    if (types.length === 0) return;
    setSelected(prev => {
      const next = { ...prev };
      let changed = false;
      for (const t of types) {
        if (next[t] === undefined) {
          const first = groups[t][0];
          if (first) { next[t] = first.alias; changed = true; }
        }
      }
      return changed ? next : prev;
    });
  }, [types, groups]);

  const selectedItems = useMemo(() => {
    const out = [];
    for (const t of types) {
      const alias = selected[t];
      if (!alias || alias === '__none__') continue;
      const item = (groups[t] || []).find(c => c.alias === alias);
      if (item) out.push({ ...item, _slot: t });
    }
    return out;
  }, [selected, types, groups]);

  const totalPrice = useMemo(() => {
    return selectedItems.reduce((s, it) => s + (parseFloat(it.price) || 0), 0)
      + (parseFloat(product?.price) || 0);
  }, [selectedItems, product]);

  const handleAddBundle = useCallback(async () => {
    if (!onAddToCart || selectedItems.length === 0) return;
    setAdding(true);
    setAdded(false);
    try {
      // Hauptprodukt + jede Komponente als eigene Position
      const all = [product, ...selectedItems];
      for (const it of all) {
        await onAddToCart(it, 1);
      }
      setAdded(true);
      setTimeout(() => setAdded(false), 3500);
    } catch (e) {
      console.error('Konfigurator Add-to-Cart Fehler:', e);
    } finally {
      setAdding(false);
    }
  }, [selectedItems, product, onAddToCart]);

  // Nur rendern, wenn es Komponenten gibt
  if (loading) {
    return (
      <div className="remus-konfigurator-loading" data-testid="remus-konfigurator-loading"
           style={{ padding: 16, color: '#888', fontSize: 13 }}>
        Lade Konfigurator …
      </div>
    );
  }
  if (components.length === 0 || types.length === 0) return null;

  // Block-Styles
  const blockStyle = {
    margin: '24px 0',
    padding: '20px 24px',
    background: '#0d0d0d',
    border: '1px solid #1f1f1f',
    borderRadius: 8,
  };

  return (
    <section
      className="remus-konfigurator"
      data-testid="remus-konfigurator"
      style={blockStyle}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6,
      }}>
        <Settings2 size={22} color="#EC2229" />
        <h2 style={{
          margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#fff',
        }}>
          Konfigurator – Anlage individuell zusammenstellen
        </h2>
      </div>
      <p style={{
        margin: '0 0 18px', color: '#999', fontSize: 13, lineHeight: 1.5,
      }}>
        Wähle für jede Komponente die passende Variante – oder <strong style={{ color: '#bbb' }}>"— ohne —"</strong>,
        wenn du das Teil nicht möchtest. Der Preis aktualisiert sich automatisch. Beim Klick auf <strong>"Konfiguration in den Warenkorb"</strong>
        werden alle Komponenten als einzelne Artikel hinzugefügt.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {types.map(type => {
          const items = groups[type] || [];
          const sel = selected[type];
          const selItem = sel && sel !== '__none__' ? items.find(c => c.alias === sel) : null;
          const isNone = sel === '__none__';
          return (
            <div key={type} data-testid={`remus-konfig-row-${type}`}
                 style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{
                fontSize: 12, color: '#bbb', fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: 0.5,
              }}>
                {type}
                <span style={{ color: '#666', marginLeft: 8, fontWeight: 400, textTransform: 'none' }}>
                  ({items.length} {items.length === 1 ? 'Option' : 'Optionen'})
                </span>
              </label>
              <select
                value={sel || ''}
                onChange={e => setSelected(prev => ({ ...prev, [type]: e.target.value }))}
                data-testid={`remus-konfig-select-${type}`}
                style={{
                  padding: '10px 12px',
                  background: isNone ? '#1a1a1a' : '#0a0a0a',
                  color: isNone ? '#888' : '#fff',
                  border: '1px solid ' + (isNone ? '#333' : '#2a2a2a'),
                  borderRadius: 4,
                  fontSize: 14,
                }}
              >
                <option value="__none__">— ohne (nicht enthalten) —</option>
                {items.map(it => (
                  <option key={it.alias} value={it.alias}>
                    {it.name} — {fmtPrice(it.price)}
                  </option>
                ))}
              </select>
              {/* Bild-Preview des ausgewählten Teils */}
              {selItem && (selItem.image || selItem.image_url) && (
                <div
                  data-testid={`remus-konfig-preview-${type}`}
                  style={{
                    display: 'flex', gap: 12, alignItems: 'center',
                    background: '#0a0a0a', border: '1px solid #1f1f1f',
                    borderRadius: 4, padding: 8, marginTop: 2,
                  }}
                >
                  <div style={{
                    width: 84, height: 64, background: '#fff',
                    borderRadius: 3, overflow: 'hidden', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <img
                      src={(() => {
                        const u = selItem.image || selItem.image_url;
                        if (!u) return '';
                        if (/^https?:\/\//i.test(u)) return u;
                        if (u.startsWith('/')) return API + u;
                        return u;
                      })()}
                      alt={selItem.name}
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      margin: 0, color: '#ddd', fontSize: 12,
                      overflow: 'hidden', textOverflow: 'ellipsis',
                      display: '-webkit-box', WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}>
                      {selItem.name}
                    </p>
                    <p style={{ margin: '2px 0 0', color: '#EC2229', fontSize: 12, fontWeight: 700 }}>
                      {fmtPrice(selItem.price)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{
        marginTop: 20, padding: '14px 16px', background: '#0a0a0a',
        border: '1px solid #1f1f1f', borderRadius: 6,
        display: 'flex', flexDirection: 'column', gap: 8,
      }} data-testid="remus-konfig-summary">
        <p style={{ margin: 0, color: '#bbb', fontSize: 13, fontWeight: 600 }}>
          Konfiguration im Detail:
        </p>
        <ul style={{ margin: 0, padding: '0 0 0 18px', color: '#aaa', fontSize: 13 }}>
          <li><strong style={{ color: '#fff' }}>{product?.name}</strong> — {fmtPrice(product?.price)}</li>
          {selectedItems.map(it => (
            <li key={it.alias}>
              <span style={{ color: '#888', marginRight: 6 }}>[{it._slot}]</span>
              {it.name} — {fmtPrice(it.price)}
            </li>
          ))}
        </ul>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 8, paddingTop: 12, borderTop: '1px solid #1f1f1f',
        }}>
          <span style={{ color: '#bbb', fontSize: 14, fontWeight: 600 }}>Gesamtpreis:</span>
          <span style={{ color: '#EC2229', fontSize: 22, fontWeight: 800 }}
                data-testid="remus-konfig-total">
            {fmtPrice(totalPrice)}
          </span>
        </div>
      </div>

      <button
        type="button"
        onClick={handleAddBundle}
        disabled={adding || selectedItems.length === 0}
        data-testid="remus-konfig-add-to-cart"
        style={{
          marginTop: 16, width: '100%', padding: '14px 20px',
          background: added ? '#16a34a' : '#EC2229', color: '#fff',
          border: 'none', borderRadius: 4, fontSize: 15, fontWeight: 700,
          cursor: adding ? 'wait' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          textTransform: 'uppercase', letterSpacing: 0.5,
          opacity: adding ? 0.6 : 1,
          transition: 'background 0.2s',
        }}
      >
        {added ? (
          <>
            <Check size={18} /> Konfiguration hinzugefügt
          </>
        ) : (
          <>
            <ShoppingCart size={18} />
            {adding ? 'Wird hinzugefügt…' : `Konfiguration in den Warenkorb (${selectedItems.length + 1} Artikel)`}
          </>
        )}
      </button>
    </section>
  );
}
